import './style.css';
import './type-ramp.js';

// Initialize Sentry for error tracking
import * as Sentry from '@sentry/browser';

// Initialize Sentry if DSN is provided
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    tracesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,
    replaysSessionSampleRate: 0.01,
    replaysOnErrorSampleRate: 1.0,
  });
}

import { DesignToken } from '@microsoft/fast-foundation';
import { AdaptiveDesignSystem } from '@adaptive-web/adaptive-web-components';
import { horizontalScrollDefinition } from '@adaptive-web/adaptive-web-components/horizontal-scroll';
import { listboxOptionDefinition } from '@adaptive-web/adaptive-web-components/listbox-option';
import { tooltipDefinition } from '@adaptive-web/adaptive-web-components/tooltip';
import { accentBaseColor, fillColor, layerFillFixedBase, neutralBaseColor, cornerRadiusControl, densityControl, fontFamily } from '@adaptive-web/adaptive-ui/reference';

import { SiteColors, addMediaListeners, darkModeQuery, setColorScheme } from './style.js';
import { Header } from './components/header/header.js';
import { SectionHeader } from './components/section-header/section-header.js';
import { Text } from './components/text/text.js';
import { VisuallyHidden } from './components/visually-hidden/visually-hidden.js';
import { Card } from './components/card/card.js';
import { CardDeck } from './components/card-deck/card-deck.js';
import { Footer } from './components/footer/footer.js';
import { ContactForm } from './components/contact-form/contact-form.js';
import { CTA } from './components/call-to-action/call-to-action.js';
import { Root } from './root/root.js';

import { css } from '@microsoft/fast-element';
import { typeRampStyles } from './type-ramp.js';
import { customCardDefintion } from './components/adaptive-card/index.js';
import { customAnchorDefinition } from './components/adaptive-anchor/index.js';
import { customButtonDefinition } from './components/adaptive-button/index.js';
import { customTextFieldDefinition } from './components/adaptive-text-field/index.js';
import { customTextAreaDefinition } from './components/adaptive-text-area/index.js';
import { customSelectDefinition } from './components/adaptive-select/index.js';
import { customAccordionItemDefinition } from './components/adaptive-accordion-item/index.js';
import { customAccordionDefinition } from './components/adaptive-accordion/index.js';
import { customFlipperDefinition } from './components/adaptive-flipper/index.js';
import { HeroCard } from './components/hero-card/hero-card.js';
import { Image } from './components/image/image.js';
import { Carousel } from './components/carousel/carousel.js';
import { Diagram } from './components/platform-diagram/diagram.js';

/**
 * Design Tokens
 */
DesignToken.registerDefaultStyleTarget();

fillColor.withDefault(layerFillFixedBase);
accentBaseColor.withDefault('#0AED8B');
neutralBaseColor.withDefault(SiteColors.neutral);
cornerRadiusControl.withDefault('6px');
densityControl.verticalPaddingUnits.withDefault(4);
densityControl.horizontalPaddingUnits.withDefault(4);

fontFamily.withDefault('"Source Sans 3", sans-serif');

/**
 * Set initial media queries and attach listeners.
 */
setColorScheme(darkModeQuery);
addMediaListeners();

/**
 * Adaptive Components
 */
AdaptiveDesignSystem.defineComponents({
  customAccordionDefinition,
  customAccordionItemDefinition,
  customAnchorDefinition,
  customButtonDefinition,
  listboxOptionDefinition,
  customSelectDefinition,
  customTextAreaDefinition,
  customTextFieldDefinition,
  tooltipDefinition,
  horizontalScrollDefinition,
  customFlipperDefinition,
  customCardDefintion
});

/**
 * Site Components
 */
VisuallyHidden;
Header;
Footer;
Text;
CTA;
Card;
CardDeck;
Carousel;
SectionHeader;
ContactForm;
HeroCard;
Image;
Diagram;
Root;

css`
  @layer type-ramp {
    ${typeRampStyles}
  }
`.addStylesTo(document);